import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Coins, CreditCard, Wallet } from "lucide-react";
import apiClient from "@/api/ApiClient";
import { isDebug } from "@/config/env";
import { StripeCheckout } from "@/components/StripeCheckout";
import { useCapacitorPlatform } from "@/hooks/use-capacitor";
import { useTranslation } from "@/hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  ensureRevenueCatUser,
  getRevenueCatTokenOffers,
  purchaseRevenueCatTokens,
  type RevenueCatTokenOffer,
} from "@/lib/revenueCatClient";
import type { TokenPackage } from "@/api/models";

/**
 * What the list shows. On the web it is the backend catalog priced in USD
 * (Stripe); on iOS/Android it is the store's consumable packs with the
 * store's localized price, matched to the catalog only for the "popular"
 * badge.
 */
interface DisplayPackage {
  id: string;
  tokens: number;
  priceLabel: string;
  /** Catalog price in USD; only known for the web catalog. */
  usdPrice: number | null;
  /** USD price per 100 tokens; only known for the web catalog. */
  pricePer100: string | null;
  popular: boolean;
  storeOffer: RevenueCatTokenOffer | null;
}

// The RevenueCat webhook credits the tokens a moment after the store confirms
// the purchase; refresh the balance a couple of times to catch it.
const BALANCE_REFRESH_DELAYS_MS = [1500, 5000];

interface TokenBalanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userTokens: number;
  onPurchaseTokens: (tokens: number, paymentMethod: string) => void;
  startAtPayment?: boolean;
}

export const TokenBalanceModal = ({
  open,
  onOpenChange,
  userTokens,
  onPurchaseTokens,
  startAtPayment
}: TokenBalanceModalProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [selectedPackageId, setSelectedPackageId] = useState<string>('');
  const [showStripeCheckout, setShowStripeCheckout] = useState(false);
  const [tokenPackages, setTokenPackages] = useState<TokenPackage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorKind, setErrorKind] = useState<'load' | 'empty' | null>(null);
  const [storeOffers, setStoreOffers] = useState<RevenueCatTokenOffer[] | null>(null);
  const [storeLoading, setStoreLoading] = useState(false);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const capacitorPlatform = useCapacitorPlatform();
  const isNative = capacitorPlatform === 'ios' || capacitorPlatform === 'android';

  useEffect(() => {
    const fetchPackages = async () => {
      if (!open) return;
      setIsLoading(true);
      setErrorKind(null);
      try {
        const res = await apiClient.getTokenPackages();
        const items = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);

        if (items.length > 0) {
          setTokenPackages(items);
        } else {
          setErrorKind('empty');
        }
      } catch (e: any) {
        console.error('Failed to load token packages from API:', e);
        setErrorKind('load');
      } finally {
        setIsLoading(false);
      }
    };
    fetchPackages();
  }, [open]);

  // Native: the store decides what can be bought and for how much.
  useEffect(() => {
    if (!open || !isNative) return;
    let cancelled = false;
    setStoreLoading(true);
    setStoreError(null);
    getRevenueCatTokenOffers()
      .then((offers) => {
        if (cancelled) return;
        setStoreOffers(offers);
        if (offers.length === 0) {
          console.warn('[RevenueCat] "tokens" offering has no purchasable packs');
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[RevenueCat] Failed to load token offering:', err);
        setStoreOffers([]);
        setStoreError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setStoreLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isNative]);

  const displayPackages = useMemo<DisplayPackage[]>(() => {
    if (!isNative) {
      return tokenPackages.map((pkg) => ({
        id: pkg.id,
        tokens: pkg.tokens,
        priceLabel: `$${pkg.price}`,
        usdPrice: pkg.price,
        pricePer100: (pkg.price / pkg.tokens * 100).toFixed(1),
        popular: Boolean(pkg.popular),
        storeOffer: null,
      }));
    }
    return (storeOffers ?? []).map((offer) => ({
      id: `store:${offer.tokens}`,
      tokens: offer.tokens,
      priceLabel: offer.priceLabel ?? (offer.price != null ? String(offer.price) : ''),
      usdPrice: null,
      pricePer100: null,
      popular: tokenPackages.some((pkg) => pkg.tokens === offer.tokens && pkg.popular),
      storeOffer: offer,
    }));
  }, [isNative, tokenPackages, storeOffers]);

  // Keep a sensible selection: the popular pack, else the middle one.
  useEffect(() => {
    if (displayPackages.length === 0) return;
    if (displayPackages.some((pkg) => pkg.id === selectedPackageId)) return;
    const popular = displayPackages.find((pkg) => pkg.popular);
    setSelectedPackageId((popular || displayPackages[Math.floor(displayPackages.length / 2)]).id);
  }, [displayPackages, selectedPackageId]);

  useEffect(() => {
    if (open && startAtPayment && selectedPackageId) {
      handleStartCheckout();
    }
  }, [open, startAtPayment, selectedPackageId]);

  const selectedPkg = displayPackages.find(p => p.id === selectedPackageId);

  const handleStartCheckout = async () => {
    if (!selectedPkg || isPurchasing) return;

    // iOS / Android: Google Play / App Store purchase through RevenueCat. The
    // webhook credits the tokens on the backend. This also runs in the TEST
    // build, so sandbox purchases can be tried end to end there.
    if (isNative) {
      await purchaseFromStore(selectedPkg);
      return;
    }

    if (isDebug) {
      onPurchaseTokens(selectedPkg.tokens, 'card');
      onOpenChange(false);
      return;
    }

    setShowStripeCheckout(true);
  };

  const purchaseFromStore = async (pkg: DisplayPackage) => {
    if (!pkg.storeOffer) {
      toast({
        title: t('tokens.error_title'),
        description: storeError ?? t('tokens.error_store_unavailable'),
        variant: 'destructive',
      });
      return;
    }
    if (!user?.id) {
      toast({
        title: t('tokens.error_title'),
        description: t('tokens.error_sign_in_purchase'),
        variant: 'destructive',
      });
      return;
    }

    setIsPurchasing(true);
    try {
      await ensureRevenueCatUser(user.id);
      const result = await purchaseRevenueCatTokens(pkg.storeOffer);
      if (result.status === 'success') {
        toast({
          title: t('tokens.purchase_success_title'),
          description: t('tokens.purchase_success_description', { count: pkg.tokens }),
        });
        for (const delay of BALANCE_REFRESH_DELAYS_MS) {
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('tokens-updated'));
          }, delay);
        }
        onOpenChange(false);
      } else if (result.status === 'cancelled') {
        console.log('[RevenueCat] Token purchase cancelled by user');
      } else {
        toast({
          title: t('tokens.error_title'),
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (err) {
      console.error('[RevenueCat] Token purchase threw unexpectedly:', err);
      toast({
        title: t('tokens.error_title'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const resetModals = () => {
    setShowStripeCheckout(false);
  };

  const handleClose = (open: boolean) => {
    onOpenChange(open);
    if (!open) {
      resetModals();
    }
  };

  const listLoading = isLoading || (isNative && storeLoading);
  const storeEmpty = isNative && !storeLoading && storeOffers !== null && storeOffers.length === 0;

  return (
    <>
      <Dialog open={open && !showStripeCheckout} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-center gap-2 text-xl">
              <Coins className="h-6 w-6 text-amber-500" />
              {t('tokens.modal_title')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('tokens.modal_description')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <Card className="p-4 bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/20">
              <div className="text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Wallet className="h-5 w-5 text-amber-500" />
                  <span className="text-lg font-semibold">{t('tokens.current_balance')}</span>
                </div>
                <div className="text-3xl font-bold text-amber-600">{userTokens}</div>
                <div className="text-sm text-muted-foreground">{t('tokens.tokens_available_sub')}</div>
              </div>
            </Card>

            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-center">{t('tokens.purchase_section_title')}</h3>
              {listLoading && (
                <div className="text-center text-sm text-muted-foreground">{t('tokens.loading_packages')}</div>
              )}
              {!isNative && errorKind === 'load' && (
                <div className="text-center text-sm text-red-600">{t('tokens.error_load_packages')}</div>
              )}
              {!isNative && errorKind === 'empty' && (
                <div className="text-center text-sm text-muted-foreground">{t('tokens.empty_no_packages')}</div>
              )}
              {storeEmpty && (
                <div className="text-center text-sm text-red-600">
                  {t('tokens.error_store_unavailable')}
                  {storeError && <div className="mt-1 text-xs text-muted-foreground">{storeError}</div>}
                </div>
              )}
              {!listLoading && !errorKind && !storeEmpty && displayPackages.length === 0 && (
                <div className="text-center text-sm text-muted-foreground">{t('tokens.empty_no_packages')}</div>
              )}
              <div className="grid gap-3">
                {displayPackages.map((pkg) => (
                  <Card
                    key={pkg.id}
                    className={`p-4 cursor-pointer transition-all border-2 relative ${
                      selectedPackageId === pkg.id
                        ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                        : 'border-primary/20 hover:border-primary/40'
                    }`}
                    onClick={() => setSelectedPackageId(pkg.id)}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <span className="bg-amber-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          {t('tokens.badge_popular')}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                          <Coins className="h-5 w-5 text-amber-500" />
                        </div>
                        <div>
                          <div className="font-semibold">
                            {t('tokens.package_count', { count: pkg.tokens })}
                          </div>
                          {pkg.pricePer100 && (
                            <div className="text-sm text-muted-foreground">
                              {t('tokens.price_per_100', { price: pkg.pricePer100 })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary">{pkg.priceLabel}</div>
                        {selectedPackageId === pkg.id && (
                          <div className="text-xs text-primary font-medium">{t('tokens.selected')}</div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Button
                variant="romantic"
                className="w-full"
                onClick={handleStartCheckout}
                disabled={!selectedPkg || isPurchasing}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {isPurchasing
                  ? t('tokens.purchasing')
                  : isNative
                    ? t('tokens.purchase_button_store', {
                        tokens: selectedPkg?.tokens ?? '',
                        price: selectedPkg?.priceLabel ?? '',
                      })
                    : t('tokens.purchase_button', {
                        tokens: selectedPkg?.tokens ?? '',
                        price: selectedPkg?.usdPrice ?? '',
                      })}
              </Button>

              <div className="text-xs text-muted-foreground text-center">
                {isNative
                  ? t(capacitorPlatform === 'ios' ? 'tokens.payment_footer_appstore' : 'tokens.payment_footer_play')
                  : t('tokens.payment_footer')}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showStripeCheckout && selectedPackageId && !isNative && (
        <Dialog open={showStripeCheckout} onOpenChange={(o) => {
          if (!o) {
            setShowStripeCheckout(false);
            resetModals();
          }
        }}>
          <DialogContent className="sm:max-w-md">
            <StripeCheckout
              packageId={selectedPackageId}
              platform="web"
              onSuccess={() => {
                setShowStripeCheckout(false);
                onOpenChange(false);
                window.dispatchEvent(new CustomEvent('tokens-updated'));
              }}
              onCancel={() => {
                setShowStripeCheckout(false);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

    </>
  );
};
